import {
  getVisualStyleLabel,
  isVisualStyleLabelId,
  type VisualStyleLabelId,
} from './vocabulary';

export interface ImageLabelAssignment {
  labelId: VisualStyleLabelId;
  confidence: number;
  evidence: string[];
}

export interface ImageLabelAnalysis {
  imageId: string;
  labels: ImageLabelAssignment[];
}

export interface CandidateStyleLabel {
  labelId: VisualStyleLabelId;
  coverage: number;
  averageConfidence: number;
  genericness: number;
  distinctiveness: number;
  redundancyPenalty: number;
  score: number;
  sourceImageIds: string[];
  evidence: string[];
}

const round = (value: number) => Math.round(value * 1000) / 1000;

const sharesMeaning = (left: VisualStyleLabelId, right: VisualStyleLabelId) => {
  const leftLabel = getVisualStyleLabel(left);
  const rightLabel = getVisualStyleLabel(right);
  if (!leftLabel || !rightLabel) return false;
  return (
    leftLabel.family === rightLabel.family ||
    leftLabel.relatedLabelIds.includes(right) ||
    rightLabel.relatedLabelIds.includes(left)
  );
};

export const aggregateStyleLabels = (analyses: ImageLabelAnalysis[]): CandidateStyleLabel[] => {
  const byLabel = new Map<
    VisualStyleLabelId,
    { confidences: number[]; imageIds: Set<string>; evidence: Set<string> }
  >();

  for (const analysis of analyses) {
    for (const assignment of analysis.labels) {
      const entry = byLabel.get(assignment.labelId) ?? {
        confidences: [],
        imageIds: new Set<string>(),
        evidence: new Set<string>(),
      };
      entry.confidences.push(assignment.confidence);
      entry.imageIds.add(analysis.imageId);
      assignment.evidence.forEach((item) => entry.evidence.add(item));
      byLabel.set(assignment.labelId, entry);
    }
  }

  const count = Math.max(1, analyses.length);
  const candidates = [...byLabel.entries()].map(([labelId, values]) => {
    const label = getVisualStyleLabel(labelId)!;
    const coverage = values.imageIds.size / count;
    const averageConfidence =
      values.confidences.reduce((sum, value) => sum + value, 0) / values.confidences.length;
    const base = coverage * averageConfidence;
    const score =
      base * (0.75 + 0.25 * label.distinctiveness) * (1 - 0.35 * label.genericness);
    return {
      labelId,
      coverage: round(coverage),
      averageConfidence: round(averageConfidence),
      genericness: label.genericness,
      distinctiveness: label.distinctiveness,
      redundancyPenalty: 0,
      score: round(score),
      sourceImageIds: [...values.imageIds],
      evidence: [...values.evidence].slice(0, 5),
    };
  });

  return candidates
    .map((candidate) => {
      const nearDuplicateCount = candidates.filter(
        (other) =>
          other.labelId !== candidate.labelId &&
          other.score >= candidate.score * 0.8 &&
          sharesMeaning(candidate.labelId, other.labelId),
      ).length;
      const redundancyPenalty = Math.min(0.24, nearDuplicateCount * 0.06);
      return {
        ...candidate,
        redundancyPenalty: round(redundancyPenalty),
        score: round(candidate.score * (1 - redundancyPenalty)),
      };
    })
    .sort(
      (left, right) =>
        right.score - left.score ||
        right.distinctiveness - left.distinctiveness ||
        left.labelId.localeCompare(right.labelId),
    );
};

const usableEditorialIds = (
  ids: string[],
  candidates: CandidateStyleLabel[],
  excluded = new Set<string>(),
) => {
  const candidateIds = new Set(candidates.map((item) => item.labelId));
  return ids.filter(
    (id, index): id is VisualStyleLabelId =>
      isVisualStyleLabelId(id) &&
      candidateIds.has(id) &&
      !excluded.has(id) &&
      ids.indexOf(id) === index,
  );
};

export const calibrateStyleLabelSelection = (
  candidates: CandidateStyleLabel[],
  aiCoreLabelIds: string[],
  aiSupportingLabelIds: string[],
) => {
  const core = usableEditorialIds(aiCoreLabelIds, candidates).slice(0, 3);
  const ranked = candidates.map((item) => item.labelId);

  for (const labelId of ranked) {
    if (core.length === 3) break;
    if (core.includes(labelId)) continue;
    const redundancy = core.filter((existing) => sharesMeaning(existing, labelId)).length;
    if (redundancy === 0 || ranked.every((candidate) => core.includes(candidate) || sharesMeaning(candidate, labelId))) {
      core.push(labelId);
    }
  }
  for (const labelId of ranked) {
    if (core.length === 3) break;
    if (!core.includes(labelId)) core.push(labelId);
  }

  const coreSet = new Set<string>(core);
  const supporting = usableEditorialIds(aiSupportingLabelIds, candidates, coreSet).slice(0, 3);
  for (const labelId of ranked) {
    if (supporting.length === 3) break;
    if (!coreSet.has(labelId) && !supporting.includes(labelId)) supporting.push(labelId);
  }

  return {
    core: core as [VisualStyleLabelId, VisualStyleLabelId, VisualStyleLabelId],
    supporting,
    adjusted:
      core.join('|') !== aiCoreLabelIds.slice(0, 3).join('|') ||
      supporting.join('|') !== aiSupportingLabelIds.slice(0, 3).join('|'),
  };
};

